/**
 * RoundExpedientesBrowser — the whole-round expediente browser embedded in the
 * Tablero (below the KPI grid). Lists EVERY expediente regardless of decision
 * (the superset of the old review queue), with a decision filter, a reason
 * (failure/flag code) filter, pagination, and a row click into the per-
 * expediente detail — which works for any status, approved included.
 *
 * Data: the round index (`GET /v1/rounds/{round_id}/expediente-index`, PRD
 * #330), downloaded once per visit; decision, motivo, order and pagination are
 * applied locally by the pure `filterSortPage` (screens/roundIndex/query.ts),
 * with the same semantics the paged endpoint had. Rows carry the EFFECTIVE
 * (adjudicated) decision. The index stays fresh on its own (issue #334,
 * useRoundIndex): an ETag probe every 15 s while the tab is visible, plus on
 * focus; `onIndexChange` fires when a probe brought a new index so the
 * dashboard can reload the report and keep the KPI cards in step.
 *
 * Search (issue #333): a free-text box filters the same index on every
 * keystroke (no request) by name, CURP or any extracted text — word-AND over
 * the server-folded `search_text` (screens/roundIndex/search.ts).
 *
 * The whole view — verdict, motivo, search and page — lives in the URL
 * (`?decision=&reason=&q=&page=`, issues #333/#336), so reload, share and
 * back-from-detail restore it (screens/roundIndex/urlState.ts).
 */

import { useMemo } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";

import { Loading } from "./Loading";
import { ProblemAlert } from "./ProblemAlert";
import { EmptyState } from "./EmptyState";
import {
  DecisionChip,
  PriorityChip,
  ReasonChips,
  REASON_OPTIONS,
} from "./decisionChips";
import { TagChips } from "./tagChips";
import type { ReviewQueueState } from "../screens/detail/queueState";
import {
  filterSortPage,
  PAGE_SIZE,
  type DecisionFilter,
  type ReasonFilter,
} from "../screens/roundIndex/query";
import { useSearchDraft } from "../screens/roundIndex/searchDraft";
import { useRoundIndex } from "../screens/roundIndex/useRoundIndex";
import { useTableroView } from "../screens/roundIndex/useTableroView";
import { isActiveQuery } from "../screens/roundIndex/search";

/** Status filter, controlled by the dashboard's KPI cards so the cards and the
 * table stay in sync. */
export type { DecisionFilter };

export function RoundExpedientesBrowser({
  roundId,
  onIndexChange,
}: {
  roundId: string;
  /** A freshness probe replaced the index (new run or adjudication). */
  onIndexChange?: () => void;
}) {
  const navigate = useNavigate();
  // Verdict (set by the dashboard's KPI cards), motivo, search and page all
  // live in the URL (#336); a filter change goes back to page 1.
  const { view, update } = useTableroView();
  const { decision, reason, q: urlQ } = view;
  // The typed text filters at once and reaches `?q=` debounced (searchDraft.ts):
  // binding the input straight to the URL dropped keystrokes. Typing must not
  // push one history entry per keystroke either.
  const [q, handleQueryChange] = useSearchDraft(urlQ, (next) =>
    update({ q: next }, { replace: true }),
  );
  // A search not yet in the URL still restarts from page 1 (applyViewChange
  // resets the URL page once it lands).
  const page = q === urlQ ? view.page : 1;

  // The whole round (cached + revalidated); every filter below is local.
  const { rows, error, loading, reload } = useRoundIndex(roundId, onIndexChange);

  const data = useMemo(
    () =>
      rows
        ? filterSortPage(rows, { decision, reason, q, page, pageSize: PAGE_SIZE })
        : null,
    [rows, decision, reason, q, page],
  );

  function handleReasonChange(event: SelectChangeEvent) {
    update({ reason: event.target.value as ReasonFilter });
  }

  const rangeLabel = useMemo(() => {
    if (!data || data.total === 0) return null;
    const start = (data.page - 1) * PAGE_SIZE + 1;
    const end = start + data.items.length - 1;
    return `${start}–${end} de ${data.total}`;
  }, [data]);

  const failureOptions = REASON_OPTIONS.filter((o) => o.kind === "failure");
  const flagOptions = REASON_OPTIONS.filter((o) => o.kind === "flag");

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Expedientes de la ronda
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Todos los expedientes de la ronda. Filtra por estado con las tarjetas de
        arriba o por motivo aquí, y abre cualquiera para ver su detalle, sin
        importar su resolución.
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            size="small"
            value={q}
            onChange={(event) => handleQueryChange(event.target.value)}
            placeholder="Buscar por nombre, CURP o texto…"
            sx={{ minWidth: { sm: 300 } }}
            slotProps={{
              htmlInput: {
                "aria-label": "Buscar expedientes por nombre, CURP o texto",
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: q ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="Limpiar búsqueda"
                      onClick={() => handleQueryChange("")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="reason-filter-label">Motivo</InputLabel>
            <Select
              labelId="reason-filter-label"
              label="Motivo"
              value={reason}
              onChange={handleReasonChange}
            >
              <MenuItem value="ALL">Todos</MenuItem>
              <ListSubheader>Motivos de rechazo</ListSubheader>
              {failureOptions.map((o) => (
                <MenuItem key={o.code} value={o.code}>
                  {o.label}
                </MenuItem>
              ))}
              <ListSubheader>Indicadores (revisión)</ListSubheader>
              {flagOptions.map((o) => (
                <MenuItem key={o.code} value={o.code}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {rangeLabel && (
          <Typography variant="body2" color="text.secondary">
            {rangeLabel}
          </Typography>
        )}
      </Stack>

      {error && <ProblemAlert error={error} onRetry={reload} />}
      {loading && <Loading label="Cargando expedientes…" />}

      {!loading && !error && rows && data && data.items.length === 0 && (
        rows.length > 0 && isActiveQuery(q) ? (
          <EmptyState
            icon={<SearchOffIcon fontSize="inherit" />}
            title="Sin coincidencias"
            description={`Ningún expediente coincide con «${q.trim()}»${
              decision === "ALL" && reason === "ALL" ? "" : " con estos filtros"
            }. Revisa la ortografía o prueba con parte del nombre o de la CURP.`}
          />
        ) : (
          <EmptyState
            icon={<FactCheckOutlinedIcon fontSize="inherit" />}
            title="Sin expedientes"
            description={
              rows.length === 0
                ? "No hay expedientes en esta ronda."
                : "Ningún expediente coincide con estos filtros. Prueba con otros."
            }
          />
        )
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <>
          <TableContainer>
            <Table size="small" aria-label="Expedientes de la ronda">
              <TableHead>
                <TableRow>
                  <TableCell>Expediente</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Motivos</TableCell>
                  <TableCell>Prioridad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((row, rowIndex) => (
                  <TableRow
                    key={row.expediente_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      // Hand the detail view a FROZEN queue snapshot so its
                      // prev/next arrows walk exactly the page the reviewer saw
                      // (a refetch would reshuffle as adjudications land).
                      const queue: ReviewQueueState = {
                        ids: data.items.map((it) => it.expediente_id),
                        index: rowIndex,
                        page: data.page,
                        pageSize: PAGE_SIZE,
                        total: data.total,
                        filters: { decision, reason, q, page: data.page },
                        roundId,
                      };
                      navigate(`/rounds/${roundId}/review/${row.expediente_id}`, {
                        state: { queue },
                      });
                    }}
                  >
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {row.expediente_id}
                    </TableCell>
                    <TableCell>{row.display_name ?? "—"}</TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        sx={{ flexWrap: "wrap", alignItems: "center" }}
                      >
                        <DecisionChip
                          decision={row.decision}
                          adjudicated={row.adjudicated}
                        />
                        {/* Operator labels (e.g. "Menor de edad"); absent for
                            most rows, so nothing renders. */}
                        <TagChips tags={row.tags} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <ReasonChips failures={row.failures} flags={row.flags} />
                    </TableCell>
                    <TableCell>
                      <PriorityChip priority={row.priority} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {data.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
              <Pagination
                count={data.totalPages}
                page={data.page}
                onChange={(_, value) => update({ page: value })}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
